import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { FacilityService } from '../servicies/facility.service';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { ReassignTherapistAvailabilitySearchRequestModel } from 'src/app/shared/models/request/therapist/reassign-therapist-availability-request-model';
import { ReassignmentDetails } from 'src/app/shared/models/response/therapist/therapist-appointments-response-model';
import moment from 'moment';
import { AppointmentTimeGroup, FinalReassignmentRow, PatientAppointment, TargetTherapistDetail } from 'src/app/interfaces/reassignmet';
import { AdminSettingsService } from '../servicies/admin-settings.service';
import { Constants } from 'src/app/shared/constants';
import { Router } from '@angular/router';
@Component({
  selector: 'app-reassignment',
  templateUrl: './reassignment.component.html',
  styleUrl: './reassignment.component.sass',
  standalone: false
})
export class ReassignmentComponent extends BaseComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<void> = new Subject<void>();

  isReassignWholeGroup: boolean = false;
  selectedSourceTherapistId: number | null = null;
  selectedDate: Date | null = null;
  appointmentGroups: AppointmentTimeGroup[] = [];
  finalRows: FinalReassignmentRow[] = [];
  saving: boolean = false;
  targetTherapistList: TargetTherapistDetail[] = [];
  navSourceTherapistName: string = '';
  /** True while the available-therapist API call is in-flight; prevents the
   *  "no therapist" warning from flashing before data arrives. */
  loadingTherapists: boolean = false;
  constructor(
    private facilityService: FacilityService,
    private webStorageService: WebStorageService,
    private settingsService: AdminSettingsService,
    private router: Router,
    messageService: MessageService
  ) {
    super(messageService);
  }

  back() {
    this.router.navigate(['/admin/calendar/view-therapist-appointments-v1'],
      {
        state: {
          appointmentDate: this.selectedDate
        }
      }
    );
  }

  ngOnInit(): void {
    const nav = history.state as {
      reassignmentSelection?: ReassignmentDetails[];
      sourceTherapistId?: number;
      sourceTherapistName?: string;
      appointmentDate?: string | Date;
      timezone?: string;
    };

    this.settingsService.getSettingsByKey(Constants.SettingKeys.AppointmentReassignmentMode)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (result) => {
          if (result?.isSuccess) {
            this.isReassignWholeGroup = result.responseModel.schSettingValue === 'EntireGroupReassignment';
            if (nav?.reassignmentSelection?.length > 0) {
              this.selectedSourceTherapistId = nav.sourceTherapistId || null;
              this.navSourceTherapistName = nav.sourceTherapistName || '';
              this.selectedDate = nav.appointmentDate ? new Date(nav.appointmentDate as string) : new Date();
              this.loadFromReassignmentDetails(nav.reassignmentSelection, this.isReassignWholeGroup);
              this.loadTherapists(nav.reassignmentSelection.map(item => item.schAppointmentId).filter(id => id != null) as number[], nav.timezone || 'America/New_York');
            }
          }
        },
        error: (err) => this.showErrorMessage(err.message)
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private loadFromReassignmentDetails(items: ReassignmentDetails[], isWholeGroup: boolean): void {
    const groupMap = new Map<string | number, ReassignmentDetails[]>();

    items.forEach(item => {
      const key = item.groupAppointmentId != null
        ? item.groupAppointmentId
        : `ind_${item.schAppointmentId}`;
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(item);
    });

    let gid = 1;
    const groups: AppointmentTimeGroup[] = [];
    groupMap.forEach(groupItems => {
      groups.push({
        groupId: gid++,
        markSourceTherapistNA: false,
        isGroupAppointment: groupItems[0].groupAppointmentId != null,
        timeSlot: groupItems[0].appointmentTime,
        selectedTargetTherapistId: null,
        patients: groupItems.map(item => ({
          patientName: this.toTitleCase(item.patientName),
          appointmentTime: item.appointmentTime,
          appointmentId: item.schAppointmentId,
          selectedTargetTherapistId: null
        }))
      });
    });

    this.appointmentGroups = groups;
    this.isReassignWholeGroup = isWholeGroup;
  }

  private loadTherapists(appointmentIds: number[], timezone: string): void {
    this.loadingTherapists = true;
    this.facilityService
      .GetAllAvailableTherapistByDateToReassign(appointmentIds.join(','), timezone)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingTherapists = false))
      )
      .subscribe({
        next: (result) => {
          if (result?.isSuccess) {
            this.targetTherapistList = result.responseModel || [];
          }
        },
        error: (err) => this.showErrorMessage(err.message)
      });
  }


  get sourceTherapistName(): string {
    return this.navSourceTherapistName || '';
  }

  /** Capitalises the first character of every word (handles "lastname, firstname" format). */
  private toTitleCase(value: string): string {
    if (!value) return value;
    return value.replace(/\b\w/g, char => char.toUpperCase());
  }

  /** True when at least one group still has patients not yet staged in the Final table. */
  get hasAnyPendingGroup(): boolean {
    return this.appointmentGroups.some(g => !this.isGroupFullyAdded(g));
  }

  /**
   * Returns deduplicated therapist dropdown options for a given time slot.
   *
   * • isReassignWholeGroup = true  → filter by startTime only, then deduplicate
   *   by therapistId (the API returns one entry per appointmentId in the group,
   *   all carrying the same therapist + availableSlotCount).
   *
   * • isReassignWholeGroup = false → additionally filter by the patient's
   *   specific appointmentId before deduplicating, so only therapists available
   *   for that individual appointment are shown.
   *
   * timeSlot format:  "11:00 AM to 11:30 AM"
   * API startTime:    "2026-06-23T11:00:00"  → formatted to "11:00 AM"
   */
  getTherapistOptionsForTimeSlot(timeSlot: string, appointmentId?: number): { label: string; value: number }[] {
    const slotStart = timeSlot.split(' to ')[0]?.trim();

    let candidates = this.targetTherapistList
      .filter(t => moment(t.startTime).format('hh:mm A') === slotStart && t.availableSlotCount > 0);

    // In individual mode, restrict to entries that belong to this patient's appointment.
    if (!this.isReassignWholeGroup && appointmentId != null) {
      candidates = candidates.filter(t => t.appointmentId === appointmentId);
    }

    // Deduplicate by therapistId — keep the first occurrence of each therapist.
    const seen = new Set<number>();
    return candidates
      .filter(t => {
        if (seen.has(t.therapistId)) return false;
        seen.add(t.therapistId);
        return true;
      })
      .map(t => ({
        label: `${t.therapistFullname} (${t.availableSlotCount}/${t.maxAppointmentCount})`,
        value: t.therapistId
      }));
  }

  private getTargetTherapistName(therapistId: number): string {
    const t = this.targetTherapistList.find(t => t.therapistId === therapistId);
    return t ? t.therapistFullname : '';
  }

  /** Returns the current availableSlotCount for a therapist at a given time slot. */
  private getAvailableSlotCount(therapistId: number, timeSlot: string): number {
    const slotStart = timeSlot.split(' to ')[0]?.trim();
    const entry = this.targetTherapistList.find(
      t => t.therapistId === therapistId && moment(t.startTime).format('hh:mm A') === slotStart
    );
    return entry?.availableSlotCount ?? 0;
  }

  /** Immutably adjusts availableSlotCount for all entries of a therapist at a specific
   *  time slot (the API returns one entry per appointmentId, all sharing the same count),
   *  so that adding/removing a 7–8am appointment does not affect the 9–10am capacity. */
  private adjustTherapistSlot(therapistId: number, delta: number, timeSlot: string): void {
    const slotStart = timeSlot.split(' to ')[0]?.trim();
    this.targetTherapistList = this.targetTherapistList.map(t =>
      t.therapistId === therapistId && moment(t.startTime).format('hh:mm A') === slotStart
        ? { ...t, availableSlotCount: Math.max(0, t.availableSlotCount + delta) }
        : t
    );
  }

  /** True when the patient's appointment is already staged in the final table */
  isPatientAdded(appointmentId: number): boolean {
    return this.finalRows.some(r => r.appointmentId === appointmentId);
  }

  /** True when every patient in the group is staged in the final table */
  isGroupFullyAdded(group: AppointmentTimeGroup): boolean {
    return group.patients.length > 0
      && group.patients.every(p => this.isPatientAdded(p.appointmentId));
  }

  /**
   * True when no earlier row in finalRows shares the same groupId.
   * Used to render the merged N/A checkbox cell in the Final table.
   */
  isFirstInFinalGroupByGroupId(row: FinalReassignmentRow, index: number): boolean {
    return this.finalRows.findIndex(r => r.groupId === row.groupId) === index;
  }

  /** Count of rows in finalRows sharing the same groupId (used for rowspan). */
  getFinalGroupRowspanByGroupId(row: FinalReassignmentRow): number {
    return this.finalRows.filter(r => r.groupId === row.groupId).length;
  }

  /**
   * True when every patient of the source group (matched by groupId) is present
   * in the Final table. For individual (non-group) appointments this is true the
   * moment their single row is staged.
   */
  isGroupCompleteInFinal(groupId: number): boolean {
    const group = this.appointmentGroups.find(g => g.groupId === groupId);
    if (!group) return true;
    return group.patients.every(p => this.isPatientAdded(p.appointmentId));
  }

  /** Propagates a N/A checkbox change to every row in the Final table with the same groupId. */
  syncMarkNAForGroup(groupId: number, value: boolean): void {
    this.finalRows = this.finalRows.map(r =>
      r.groupId === groupId ? { ...r, markSourceTherapistNA: value } : r
    );
  }

  addGroupToFinal(group: AppointmentTimeGroup): void {
    if (!group.selectedTargetTherapistId) {
      this.showWarningMessage('Please select a target therapist for this group.');
      return;
    }

    // Determine how many new patients would actually be added (exclude already-staged ones).
    const patientsToAdd = group.patients.filter(
      p => !this.finalRows.some(r => r.appointmentId === p.appointmentId)
    );
    if (patientsToAdd.length === 0) {
      this.showWarningMessage('All patients in this group are already in the list.');
      return;
    }

    // Block if the therapist does not have enough available slots.
    const available = this.getAvailableSlotCount(group.selectedTargetTherapistId, group.timeSlot);
    if (patientsToAdd.length > available) {
      this.showWarningMessage(
        `Not enough slots available. "${this.getTargetTherapistName(group.selectedTargetTherapistId)}" ` +
        `has ${available} slot(s) available but ${patientsToAdd.length} patient(s) need to be added.`
      );
      return;
    }

    const targetName = this.getTargetTherapistName(group.selectedTargetTherapistId);
    let added = 0;
    patientsToAdd.forEach(p => {
      this.finalRows = [...this.finalRows, {
        sourceTherapistName: this.sourceTherapistName,
        patientName: p.patientName,
        appointmentTime: group.timeSlot,
        targetTherapistId: group.selectedTargetTherapistId,
        targetTherapistName: targetName,
        appointmentId: p.appointmentId,
        markSourceTherapistNA: group.markSourceTherapistNA,
        groupId: group.groupId
      }];
      added++;
    });
    if (added > 0) {
      this.adjustTherapistSlot(group.selectedTargetTherapistId, -added, group.timeSlot);
      this.showSuccessMessage(`${added} appointment(s) added to reassignment list.`);
    }
  }

  addPatientToFinal(group: AppointmentTimeGroup, patient: PatientAppointment): void {
    if (!patient.selectedTargetTherapistId) {
      this.showWarningMessage('Please select a target therapist for this patient.');
      return;
    }
    if (this.finalRows.some(r => r.appointmentId === patient.appointmentId)) {
      this.showWarningMessage(`${patient.patientName} is already in the reassignment list.`);
      return;
    }

    // Block if the therapist has no available slots left for this time slot.
    const available = this.getAvailableSlotCount(patient.selectedTargetTherapistId, patient.appointmentTime);
    if (available <= 0) {
      this.showWarningMessage(
        `Please select a target therapist for this patient.`
      );
      return;
    }

    const targetName = this.getTargetTherapistName(patient.selectedTargetTherapistId);
    const newRow: FinalReassignmentRow = {
      sourceTherapistName: this.sourceTherapistName,
      patientName: patient.patientName,
      appointmentTime: patient.appointmentTime,
      targetTherapistId: patient.selectedTargetTherapistId,
      targetTherapistName: targetName,
      appointmentId: patient.appointmentId,
      markSourceTherapistNA: false,
      groupId: group.groupId
    };
    const lastSameGroupIdx = this.finalRows.reduce<number>(
      (last, r, i) => r.groupId === group.groupId ? i : last, -1
    );
    if (lastSameGroupIdx >= 0) {
      this.finalRows = [
        ...this.finalRows.slice(0, lastSameGroupIdx + 1),
        newRow,
        ...this.finalRows.slice(lastSameGroupIdx + 1)
      ];
    } else {
      this.finalRows = [...this.finalRows, newRow];
    }
    this.adjustTherapistSlot(patient.selectedTargetTherapistId, -1, patient.appointmentTime);
    this.showSuccessMessage(`${patient.patientName} added to the reassignment list.`);
  }

  removeFinalRow(index: number): void {
    const row = this.finalRows[index];
    if (row) this.adjustTherapistSlot(row.targetTherapistId, 1, row.appointmentTime);
    this.finalRows = this.finalRows.filter((_, i) => i !== index);
  }

  removeGroupFromFinal(row: FinalReassignmentRow): void {
    const toRemove = this.finalRows.filter(
      r => r.appointmentTime === row.appointmentTime && r.targetTherapistId === row.targetTherapistId
    );
    this.adjustTherapistSlot(row.targetTherapistId, toRemove.length, row.appointmentTime);
    this.finalRows = this.finalRows.filter(
      r => !(r.appointmentTime === row.appointmentTime && r.targetTherapistId === row.targetTherapistId)
    );
  }

  isFirstInFinalGroup(row: FinalReassignmentRow, index: number): boolean {
    if (index === 0) return true;
    const prev = this.finalRows[index - 1];
    return prev.appointmentTime !== row.appointmentTime || prev.targetTherapistId !== row.targetTherapistId;
  }

  getFinalGroupRowspan(row: FinalReassignmentRow): number {
    return this.finalRows.filter(
      r => r.appointmentTime === row.appointmentTime && r.targetTherapistId === row.targetTherapistId
    ).length;
  }

  clearAll(): void {
    this.finalRows.forEach(r => this.adjustTherapistSlot(r.targetTherapistId, 1, r.appointmentTime));
    this.finalRows = [];
  }

  saveAll(): void {
    if (this.finalRows.length === 0) {
      this.showWarningMessage('No reassignments to save.');
      return;
    }
    const loggedInUser = this.webStorageService.getCurrentUserToken();
    const modifiedById = loggedInUser?.userId || 0;
    const branchId = loggedInUser?.branchId || 0;

    const grouped = new Map<string, FinalReassignmentRow[]>();
    this.finalRows.forEach(r => {
      const key = `${r.sourceTherapistName}|${r.targetTherapistId}|${r.appointmentTime}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(r);
    });
    const requests: ReassignTherapistAvailabilitySearchRequestModel[] = [];
    grouped.forEach((rows, key) => {
      const req = new ReassignTherapistAvailabilitySearchRequestModel();
      req.appointmentDate = moment(this.selectedDate).format('MM-DD-YYYY');
      req.crmTherapistId = this.selectedSourceTherapistId;
      req.crmNewTherapistId = rows[0].targetTherapistId;
      req.schAppointmentIds = rows.map(r => r.appointmentId);
      req.modifiedById = modifiedById;
      req.crmBranchIds = branchId ? [branchId] : [];
      req.isMakeUnAvailable = rows.some(r => r.markSourceTherapistNA);
      req.crmPatientTimeZone = '';
      req.crmFacilityTimeZone = '';
      requests.push(req);
    });
    this.saving = true;
    this.facilityService
      .ReassignALLTherapistAppointments(requests)
      .pipe(takeUntil(this.unsubscribe), finalize(() => (this.saving = false)))
      .subscribe({
        next: (result) => {
          if (result?.isSuccess) {
            this.showSuccessMessage('All appointments reassigned successfully.');
            this.finalRows = [];
            this.appointmentGroups = [];
            this.back();
          } else {
            this.showErrorMessage(result?.message || 'Failed to reassign appointments.');
          }
        },
        error: (err) => this.showErrorMessage(err.message)
      });
  }
}



